import React, { createContext, useContext, ReactNode } from "react";
import { useAuth } from "./AuthContext";
import { trpcReact } from "../trpc_client";

interface ProfileData {
  user_id?: number;
  username?: string;
  profile_images?: string[];
  birthdate?: string;
  profile_text?: string;
}

interface ProfileContextType {
  profile: ProfileData | null;
  isLoading: boolean;
  isProfileComplete: boolean;
  needsOnboarding: boolean;
  userExistsInDb: boolean;
  refetchProfile: () => void;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error("useProfile must be used within a ProfileProvider");
  }
  return context;
};

interface ProfileProviderProps {
  children: ReactNode;
}

export const ProfileProvider: React.FC<ProfileProviderProps> = ({
  children,
}) => {
  const { user } = useAuth();

  // Check if user exists in database - this is the gate for all other queries
  const { data: userExists, isLoading: checkingUser } =
    trpcReact.checkUserExists.useQuery(undefined, {
      enabled: !!user,
    });

  // Only fetch profile if user exists in database
  const userExistsInDb = !!user && userExists?.exists === true;

  // Get user profile if they exist
  const {
    data: profile,
    isLoading: loadingProfile,
    refetch: refetchProfile,
  } = trpcReact.getCurrentUserProfile.useQuery(undefined, {
    enabled: userExistsInDb,
  });

  const isLoading =
    !!user && (checkingUser || (userExistsInDb && loadingProfile));

  // User needs onboarding if they're logged in but don't have a profile in the database
  const needsOnboarding =
    !!user && !checkingUser && userExists?.exists === false;

  // Profile is complete if user has username and at least one profile image
  const isProfileComplete =
    userExistsInDb &&
    !!profile &&
    !!profile.username &&
    profile.username.trim() !== "" &&
    Array.isArray(profile.profile_images) &&
    profile.profile_images.length > 0;
  const userIdFromExists =
    userExists && "user_id" in userExists ? userExists.user_id : undefined;

  const value: ProfileContextType = {
    profile: profile ? { ...profile, user_id: userIdFromExists } : null,
    isLoading,
    isProfileComplete,
    needsOnboarding,
    userExistsInDb,
    refetchProfile,
  };

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  );
};

export default ProfileContext;
